// 058A — Escalas de Valoración para Cuidados Paliativos (Fase 5, Tanda 7, ver
// PLAN.md). Transcripción verbatim del PDF oficial (páginas 214-216, footer
// `SNS-MSP/HCU-form.058A/2021`): 6 escalas — KPS/PPS, Barthel, Pfeiffer, DME,
// Gijón (abreviado y modificado, Barcelona) y Zarit. El instructivo (págs.
// 211-213) NO fija reglas de puntuación adicionales; estas son las decisiones
// para lo que el papel deja abierto, todas visibles en pantalla:
//
// - Pfeiffer: el papel imprime las 4 categorías (Normal / Leve / Moderado /
//   Severo deterioro cognitivo) SIN cortes numéricos. No se inventan: el total
//   de errores (0-10) se calcula y la categoría se MARCA a mano, como en el
//   papel (mismo criterio que Braden-Bergstom/TISS-28 de Fase 4).
// - Zarit: las 5 columnas (Nunca...Casi siempre) no traen número impreso. Se
//   puntúan 1-5 (total 22-110): los cortes impresos (≤46 / 47-55 / ≥56) son
//   los de la validación española de Martín 1996 sobre ese rango — el Zarit
//   original de 0-4 usa otros cortes. Los números van en la etiqueta de cada
//   opción para que sea transparente.
// - Barthel, Gijón y DME usan los puntajes y cortes tal cual están impresos.
//   En Barthel el papel corta "5 independiente en silla de…" al borde de la
//   celda; se completa como "silla de ruedas" (única lectura posible).
// - KPS/PPS es una tabla de una sola fila a elegir (no suma de ítems): se
//   elige el % y se muestran las columnas PPS de esa fila.
//
// FHIR: ninguna. Las escalas no tienen un consumidor demostrable hoy; quedan
// como `Par[]` en el PDF firmado (el bloque M de 058 registra los resultados).
import { paresDeEscalaClinica, type DefinicionEscalaClinica, type OpcionEscalaClinica, type RespuestasEscalaClinica } from './uci/EscalaClinica';
import { OPCIONES_SI_NO, paresDeCampos, valoresVacios, type CampoSimple, type OpcionCampo, type Valores } from './camposSimplesDatos';
import type { Par } from './index';
import { CAMPOS_PROFESIONAL_PAPEL, estadoInicialProfesionalMultiple, paresProfesionalPorTurno, type EstadoProfesionalMultiple } from './profesionalPorTurnoDatos';
import type { Traducir } from './tecnicaAnestesicaDatos';

const op = (valor: number, etiqueta: string): OpcionEscalaClinica => ({ valor, etiqueta: `${valor} ${etiqueta}` });

// ---------- B. KPS / PPS ----------

export interface FilaKpsPps {
  readonly porcentaje: number;
  readonly kps: string;
  readonly ambulacion: string;
  readonly actividad: string;
  readonly evidencia: string;
  readonly autocuidado: string;
  readonly ingesta: string;
  readonly conciencia: string;
}

export const TABLA_KPS_PPS: readonly FilaKpsPps[] = [
  { porcentaje: 100, kps: 'Normal, no quejas o evidencia de enfermedad', ambulacion: 'Plena', actividad: 'Normal', evidencia: 'No', autocuidado: 'Total', ingesta: 'Normal', conciencia: 'Plena' },
  { porcentaje: 90, kps: 'Actividad normal, signos o síntomas menores de enfermedad', ambulacion: 'Plena', actividad: 'Normal', evidencia: 'Alguna', autocuidado: 'Total', ingesta: 'Normal', conciencia: 'Plena' },
  { porcentaje: 80, kps: 'Actividad normal con esfuerzo, algunos signos o síntomas de enfermedad', ambulacion: 'Plena', actividad: 'Normal con esfuerzo', evidencia: 'Alguna', autocuidado: 'Total', ingesta: 'Normal o reducida', conciencia: 'Plena' },
  { porcentaje: 70, kps: 'Cuida de sí mismo, incapaz de actividades normales o trabajo normal', ambulacion: 'Reducida', actividad: 'Incapaz de trabajar', evidencia: 'Alguna', autocuidado: 'Total', ingesta: 'Normal o reducida', conciencia: 'Plena' },
  { porcentaje: 60, kps: 'Requiere ayuda ocasional, pero es capaz de muchas actividades', ambulacion: 'Reducida', actividad: 'Incapaz de tareas', evidencia: 'Significativa', autocuidado: 'Ayuda ocasional', ingesta: 'Normal o reducida', conciencia: 'Plena o confusa' },
  { porcentaje: 50, kps: 'Requiere considerable asistencia médica y cuidado frecuente', ambulacion: 'Más sentado o en cama', actividad: 'Incapaz cualquier', evidencia: 'Enfermedad extensa', autocuidado: 'Necesita ayuda', ingesta: 'Normal o reducida', conciencia: 'Plena, somnolencia o confusión' },
  { porcentaje: 40, kps: 'Incapaz, requiere cuidado especial y asistencia', ambulacion: 'Más en cama', actividad: 'Necesita ayuda', evidencia: 'Enfermedad extensa', autocuidado: 'Mucha asistencia', ingesta: 'Normal o reducida', conciencia: 'Plena, somnolencia o confusión' },
  { porcentaje: 30, kps: 'Severamente incapacitado, hospitalización indicada aunque la muerte no sea inminente', ambulacion: 'Encamado', actividad: 'Requiere ayuda', evidencia: 'Enfermedad extensa', autocuidado: 'Cuidado total', ingesta: 'Reducida', conciencia: 'Plena, somnolencia o confusión' },
  { porcentaje: 20, kps: 'Muy enfermo. Necesita hospitalización y tratamiento de soporte', ambulacion: 'Encamado', actividad: 'Requiere ayuda', evidencia: 'Enfermedad extensa', autocuidado: 'Cuidado total', ingesta: 'Mínima, sorbos', conciencia: 'Plena, somnolencia o confusión' },
  { porcentaje: 10, kps: 'Moribundo, proceso final, que progresa rápidamente', ambulacion: 'Encamado', actividad: 'Requiere ayuda', evidencia: 'Enfermedad extensa', autocuidado: 'Cuidado total', ingesta: 'Cuidados boca', conciencia: 'Somnoliento o en coma' },
  { porcentaje: 0, kps: 'Muerto', ambulacion: 'Muerto', actividad: '', evidencia: '', autocuidado: '', ingesta: '', conciencia: '' },
];

export const OPCIONES_KPS_PPS: readonly OpcionCampo[] = TABLA_KPS_PPS.map((f) => ({ valor: String(f.porcentaje), etiqueta: `${f.porcentaje}% — ${f.kps}` }));

export function filaKpsPps(porcentaje: string): FilaKpsPps | undefined {
  return TABLA_KPS_PPS.find((f) => String(f.porcentaje) === porcentaje);
}

/** Columnas PPS de la fila elegida, en el orden del papel, sin las vacías (la fila 0% solo trae ambulación). */
export function columnasPps(fila: FilaKpsPps): readonly { readonly etiqueta: string; readonly valor: string }[] {
  return [
    { etiqueta: 'PPS — ambulación', valor: fila.ambulacion },
    { etiqueta: 'PPS — nivel de actividad', valor: fila.actividad },
    { etiqueta: 'PPS — evidencia de enfermedad', valor: fila.evidencia },
    { etiqueta: 'PPS — autocuidado', valor: fila.autocuidado },
    { etiqueta: 'PPS — ingesta', valor: fila.ingesta },
    { etiqueta: 'PPS — conciencia', valor: fila.conciencia },
  ].filter((c) => c.valor !== '');
}

// ---------- C. Barthel ----------

const IND_AYUDA_DEP = (independiente: number): readonly OpcionEscalaClinica[] => [op(independiente, 'independiente'), op(independiente / 2, 'necesita ayuda'), op(0, 'dependiente')];
const CONTINENCIA: readonly OpcionEscalaClinica[] = [op(10, 'continente'), op(5, 'accidente ocasional'), op(0, 'incontinente')];

export const DEFINICION_BARTHEL: DefinicionEscalaClinica = {
  items: [
    { clave: 'comer', etiqueta: 'Comer', opciones: IND_AYUDA_DEP(10) },
    { clave: 'lavarse', etiqueta: 'Lavarse', opciones: [op(5, 'independiente'), op(0, 'dependiente')] },
    { clave: 'arreglarse', etiqueta: 'Arreglarse', opciones: [op(5, 'independiente'), op(0, 'dependiente')] },
    { clave: 'vestirse', etiqueta: 'Vestirse', opciones: IND_AYUDA_DEP(10) },
    { clave: 'miccion', etiqueta: 'Micción', opciones: CONTINENCIA },
    { clave: 'deposicion', etiqueta: 'Deposición', opciones: CONTINENCIA },
    { clave: 'irAlWc', etiqueta: 'Ir al WC', opciones: IND_AYUDA_DEP(10) },
    { clave: 'trasladarse', etiqueta: 'Trasladarse sillón/cama', opciones: [op(15, 'independiente'), op(10, 'mínima ayuda'), op(5, 'gran ayuda'), op(0, 'dependiente')] },
    { clave: 'deambulacion', etiqueta: 'Deambulación', opciones: [op(15, 'independiente'), op(10, 'necesita ayuda'), op(5, 'independiente en silla de ruedas'), op(0, 'dependiente')] },
    { clave: 'gradas', etiqueta: 'Subir y bajar gradas', opciones: IND_AYUDA_DEP(10) },
  ],
  // Impreso: Independiente 100 · Dependiente leve ≥60 · moderado 55/40 · severo 35/20 · total <20 (el total es múltiplo de 5).
  rangos: [
    { min: 100, max: 100, etiqueta: 'Independiente' },
    { min: 60, max: 95, etiqueta: 'Dependiente leve' },
    { min: 40, max: 55, etiqueta: 'Dependiente moderado' },
    { min: 20, max: 35, etiqueta: 'Dependiente severo' },
    { min: 0, max: 15, etiqueta: 'Dependiente total' },
  ],
};

// ---------- D. Pfeiffer ----------

const ERROR: readonly OpcionEscalaClinica[] = [
  { valor: 0, etiqueta: '0 — sin error' },
  { valor: 1, etiqueta: '1 — error' },
];

const ITEMS_PFEIFFER_ANTES = [
  { clave: 'p1', etiqueta: '1. ¿Qué fecha es hoy? (día, mes, año)', opciones: ERROR },
  { clave: 'p2', etiqueta: '2. ¿Qué día de la semana es hoy?', opciones: ERROR },
  { clave: 'p3', etiqueta: '3. ¿Dónde estamos ahora?', opciones: ERROR },
] as const;

const ITEM_PFEIFFER_TELEFONO = { clave: 'p4', etiqueta: '4. ¿Cuál es su número de teléfono?', opciones: ERROR } as const;
const ITEM_PFEIFFER_DIRECCION = { clave: 'p4a', etiqueta: '4 A. Preguntar solo si el paciente no tiene teléfono: ¿Cuál es su dirección?', opciones: ERROR } as const;

const ITEMS_PFEIFFER_DESPUES = [
  { clave: 'p5', etiqueta: '5. ¿Cuántos años tiene?', opciones: ERROR },
  { clave: 'p6', etiqueta: '6. ¿En qué fecha nació usted? (día, mes, año)', opciones: ERROR },
  { clave: 'p7', etiqueta: '7. ¿Cómo se llama el presidente actual?', opciones: ERROR },
  { clave: 'p8', etiqueta: '8. ¿Quién era el presidente anterior?', opciones: ERROR },
  { clave: 'p9', etiqueta: '9. ¿Cuáles son los dos apellidos de su madre?', opciones: ERROR },
  { clave: 'p10', etiqueta: '10. Si a 20 le restamos 3 quedan...? Y si le quitamos 3...', opciones: ERROR },
] as const;

/** La pregunta 4 se reemplaza por la 4 A si el paciente no tiene teléfono (son 10 respuestas en total, nunca 11). Sin cortes numéricos impresos → `rangos: []`. */
export function definicionPfeiffer(sinTelefono: boolean): DefinicionEscalaClinica {
  return { items: [...ITEMS_PFEIFFER_ANTES, sinTelefono ? ITEM_PFEIFFER_DIRECCION : ITEM_PFEIFFER_TELEFONO, ...ITEMS_PFEIFFER_DESPUES], rangos: [] };
}

export const OPCIONES_CATEGORIA_PFEIFFER: readonly OpcionCampo[] = [
  { valor: 'normal', etiqueta: 'Normal' },
  { valor: 'leve', etiqueta: 'Leve deterioro cognitivo' },
  { valor: 'moderado', etiqueta: 'Moderado deterioro cognitivo' },
  { valor: 'severo', etiqueta: 'Severo deterioro cognitivo' },
];

// ---------- B (hoja 2). DME ----------

const cero10 = (extremoMenor: string, extremoMayor: string): readonly OpcionCampo[] =>
  Array.from({ length: 11 }, (_, i) => ({ valor: String(i), etiqueta: i === 0 ? `0 — ${extremoMenor}` : i === 10 ? `10 — ${extremoMayor}` : String(i) }));

const casilla = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'casilla' });

export const CAMPOS_DME: readonly CampoSimple[] = [
  { clave: 'animo', etiqueta: '1. ¿Cómo se encuentra de ánimo? Entre 0 “muy mal” y 10 “muy bien”, ¿qué valor le daría?', tipo: 'select', opciones: cero10('muy mal', 'muy bien') },
  { clave: 'preocupa', etiqueta: '2. ¿Hay algo que le preocupe?', tipo: 'select', opciones: OPCIONES_SI_NO },
  casilla('preocupaEconomicos', 'Tipo de preocupación — económicos'),
  casilla('preocupaFamiliares', 'Tipo de preocupación — familiares'),
  casilla('preocupaEmocionales', 'Tipo de preocupación — emocionales'),
  casilla('preocupaEspirituales', 'Tipo de preocupación — espirituales'),
  casilla('preocupaSomaticos', 'Tipo de preocupación — somáticos'),
  casilla('preocupaOtros', 'Tipo de preocupación — otros'),
  { clave: 'preocupaDescribe', etiqueta: '2. Describa (qué es lo que más le preocupa)', tipo: 'area' },
  { clave: 'lleva', etiqueta: '3. ¿Cómo lleva esta situación? Entre 0 “no le cuesta nada” y 10 “le cuesta mucho”, ¿qué valor le daría?', tipo: 'select', opciones: cero10('no le cuesta nada', 'le cuesta mucho') },
  { clave: 'signos', etiqueta: '4. ¿Se observan signos externos de malestar?', tipo: 'select', opciones: OPCIONES_SI_NO },
  casilla('signosExpresionFacial', 'Signos — expresión facial (tristeza, miedo, euforia, enfado...)'),
  casilla('signosAislamiento', 'Signos — aislamiento (mutismo, demanda de persianas bajadas, rechazo de visitas, ausencia de distracciones, incomunicación...)'),
  casilla('signosDemandaConstante', 'Signos — demanda constante de compañía / atención (quejas constantes...)'),
  casilla('signosComportamientoNocturno', 'Signos — alteraciones del comportamiento nocturno (insomnio, pesadillas, demandas de rescate no justificadas, quejas...)'),
  casilla('signosOtros', 'Signos — otros'),
  { clave: 'signosDescribe', etiqueta: '4. Describa / otros', tipo: 'area' },
  { clave: 'observaciones', etiqueta: 'Observaciones', tipo: 'area' },
];

export const CORTE_DME = 9;

/** Criterio impreso: DME = (10 − ítem 1) + ítem 3. `undefined` si falta cualquiera de los 2 ítems (sin resultado parcial). */
export function totalDme(v: Valores): number | undefined {
  const animo = v.animo === undefined || v.animo === '' ? undefined : Number(v.animo);
  const lleva = v.lleva === undefined || v.lleva === '' ? undefined : Number(v.lleva);
  if (animo === undefined || lleva === undefined || Number.isNaN(animo) || Number.isNaN(lleva)) return undefined;
  return 10 - animo + lleva;
}

// ---------- C (hoja 2). Gijón ----------

export const DEFINICION_GIJON: DefinicionEscalaClinica = {
  items: [
    {
      clave: 'situacionFamiliar',
      etiqueta: 'Situación familiar',
      opciones: [
        op(1, 'Vive con pareja y/o familia sin conflicto'),
        op(2, 'Vive con pareja de similar edad'),
        op(3, 'Vive con pareja y/o familia y/u otros, pero no pueden o no quieren atenderlo'),
        op(4, 'Vive solo, hijos y/o familiares próximos que no cubren todas las necesidades'),
        op(5, 'Vive solo, familia lejana, desatendido, sin familia'),
      ],
    },
    {
      clave: 'relacionesSociales',
      etiqueta: 'Relaciones y contactos sociales',
      opciones: [
        op(1, 'Mantiene relaciones sociales fuera del domicilio'),
        op(2, 'Sólo se relaciona con familia/vecinos/otros, sale de casa'),
        op(3, 'Sólo se relaciona con familia, sale de casa'),
        op(4, 'No sale de su domicilio, recibe familia o visitas (> 1 por semana)'),
        op(5, 'No sale del domicilio, ni recibe visitas (< 1 por semana)'),
      ],
    },
    {
      clave: 'apoyosRedSocial',
      etiqueta: 'Apoyos red social',
      opciones: [
        op(1, 'No necesita ningún apoyo'),
        op(2, 'Recibe apoyo de la familia y/o vecinos'),
        op(3, 'Recibe apoyo social formal suficiente (centro de día, trabajador/a familiar, vive en residencia, etc.)'),
        op(4, 'Tiene soporte social pero es insuficiente'),
        op(5, 'No tiene ningún soporte social y lo necesita'),
      ],
    },
  ],
  rangos: [
    { min: 0, max: 7, etiqueta: 'Situación social buena (bajo riesgo institucionalización)' },
    { min: 8, max: 9, etiqueta: 'Situación intermedia' },
    { min: 10, max: 15, etiqueta: 'Deterioro social severo (alto riesgo institucionalización)' },
  ],
};

// ---------- B (hoja 3). Zarit ----------

const FRECUENCIA_ZARIT: readonly OpcionEscalaClinica[] = [
  { valor: 1, etiqueta: 'Nunca (1)' },
  { valor: 2, etiqueta: 'Rara vez (2)' },
  { valor: 3, etiqueta: 'Algunas veces (3)' },
  { valor: 4, etiqueta: 'Bastantes veces (4)' },
  { valor: 5, etiqueta: 'Casi siempre (5)' },
];

const PREGUNTAS_ZARIT: readonly string[] = [
  '¿Piensa que su familiar le pide más ayuda de la que realmente necesita?',
  '¿Piensa que debido al tiempo que dedica a su familiar no tiene suficiente tiempo para usted?',
  '¿Se siente agobiado por intentar compatibilizar el cuidado de su familiar con otras responsabilidades (trabajo, familia)?',
  '¿Siente vergüenza por la conducta de su familiar?',
  '¿Se siente enfadado cuando esta cerca de su familiar?',
  '¿Piensa que el cuidar de su familiar afecta negativamente la relación que usted tiene con otros miembros de su familia?',
  '¿Tiene miedo por el futuro de su familiar?',
  '¿Piensa que su familiar depende de usted?',
  '¿Se siente tenso cuando está cerca de su familiar?',
  '¿Piensa que su salud ha empeorado debido a tener que cuidar de su familiar?',
  '¿Piensa que no tiene tanta intimidad como le gustaría debido a tener que cuidar de su familiar?',
  '¿Piensa que su actividad social se ha visto afectada negativamente por tener que cuidar de su familiar?',
  '¿Se siente incómodo por distanciarse de sus amistades debido a tener que cuidar de su familiar?',
  '¿Piensa que su familiar le considera a usted la única persona que le puede cuidar?',
  '¿Piensa que no tiene suficientes ingresos económicos para los gastos de cuidar a su familiar, además de sus otros gastos?',
  '¿Piensa que no será capaz de cuidar a su familiar por mucho más tiempo?',
  '¿Se siente que ha perdido el control de su vida desde que comenzó la enfermedad de su familiar?',
  '¿Desearía poder dejar el cuidado de su familiar a otra persona?',
  '¿Se siente indeciso sobre que hacer con su familiar?',
  '¿Piensa que debería hacer más por su familiar?',
  '¿Piensa que podría cuidar mejor a su familiar?',
  'De forma general, ¿Con qué frecuencia cree usted que cuidar a su familiar constituye una carga?',
];

export const DEFINICION_ZARIT: DefinicionEscalaClinica = {
  items: PREGUNTAS_ZARIT.map((texto, i) => ({ clave: `z${i + 1}`, etiqueta: `${i + 1}. ${texto}`, opciones: FRECUENCIA_ZARIT })),
  rangos: [
    { min: 0, max: 46, etiqueta: 'Ausencia de sobrecarga' },
    { min: 47, max: 55, etiqueta: 'Sobrecarga ligera' },
    { min: 56, max: 110, etiqueta: 'Sobrecarga intensa' },
  ],
};

// ---------- Estado ----------

// C. Datos del profesional responsable: el papel lo repite en cada una de sus 3 hojas. Un documento lo firma UNA persona: el servidor arma
// los datos de quien firma (marca `MARCA_FIRMANTE_SERVIDOR`, en la hoja que se marque) y las otras hojas se capturan como texto
// estructurado — mismo mecanismo que 113 K / 114 M (`profesionalPorTurnoDatos.ts`, T6b 2026-09-24).
export const HOJAS_PROFESIONAL_058A = ['hoja1', 'hoja2', 'hoja3'] as const;
export type HojaProfesional058A = (typeof HOJAS_PROFESIONAL_058A)[number];

export interface EstadoEscalasPaliativos {
  readonly kpsPps: string;
  readonly barthelFecha: string;
  readonly barthel: RespuestasEscalaClinica;
  readonly pfeifferFecha: string;
  readonly pfeifferSinTelefono: boolean;
  readonly pfeiffer: RespuestasEscalaClinica;
  readonly pfeifferCategoria: string;
  readonly dme: Valores;
  readonly gijonFecha: string;
  readonly gijon: RespuestasEscalaClinica;
  readonly zarit: RespuestasEscalaClinica;
  /** C. Profesional responsable de cada una de las 3 hojas del papel; el que firma lo arma el servidor. */
  readonly profesional: EstadoProfesionalMultiple<HojaProfesional058A>;
}

export function estadoInicialEscalasPaliativos(): EstadoEscalasPaliativos {
  return {
    kpsPps: '',
    barthelFecha: '',
    barthel: {},
    pfeifferFecha: '',
    pfeifferSinTelefono: false,
    pfeiffer: {},
    pfeifferCategoria: '',
    dme: valoresVacios(CAMPOS_DME),
    gijonFecha: '',
    gijon: {},
    zarit: {},
    profesional: estadoInicialProfesionalMultiple(HOJAS_PROFESIONAL_058A),
  };
}

/** Serializa las 6 escalas a `Par[]` — filtra respuestas vacías. Los totales solo aparecen con la escala completa. */
export function paresDeEscalasPaliativos(t: Traducir, e: EstadoEscalasPaliativos): Par[] {
  const kps = filaKpsPps(e.kpsPps);
  const pares: Par[] = [];

  if (kps) {
    pares.push({ pregunta: t('formularios.f058a.kpsPpsTitulo'), respuesta: `${kps.porcentaje}% — ${kps.kps}` });
    for (const c of columnasPps(kps)) pares.push({ pregunta: `${t('formularios.f058a.kpsPpsTitulo')} — ${c.etiqueta}`, respuesta: c.valor });
  }

  pares.push({ pregunta: `${t('formularios.f058a.barthelTitulo')} — ${t('formularios.f058a.fecha')}`, respuesta: e.barthelFecha });
  pares.push(...paresDeEscalaClinica(DEFINICION_BARTHEL, e.barthel, `${t('formularios.f058a.barthelTitulo')} — ${t('formularios.f058a.total')}`));

  const defPfeiffer = definicionPfeiffer(e.pfeifferSinTelefono);
  pares.push({ pregunta: `${t('formularios.f058a.pfeifferTitulo')} — ${t('formularios.f058a.fecha')}`, respuesta: e.pfeifferFecha });
  pares.push(...paresDeEscalaClinica(defPfeiffer, e.pfeiffer, `${t('formularios.f058a.pfeifferTitulo')} — ${t('formularios.f058a.totalErrores')}`));
  pares.push({ pregunta: `${t('formularios.f058a.pfeifferTitulo')} — ${t('formularios.f058a.categoriaPfeiffer')}`, respuesta: OPCIONES_CATEGORIA_PFEIFFER.find((o) => o.valor === e.pfeifferCategoria)?.etiqueta ?? '' });

  pares.push(...paresDeCampos(CAMPOS_DME, e.dme, `${t('formularios.f058a.dmeTitulo')} — `));
  const dme = totalDme(e.dme);
  if (dme !== undefined) {
    pares.push({ pregunta: `${t('formularios.f058a.dmeTitulo')} — ${t('formularios.f058a.dmeTotal')}`, respuesta: `${dme} — ${dme >= CORTE_DME ? t('formularios.f058a.dmePositivo') : t('formularios.f058a.dmeNegativo')}` });
  }

  pares.push({ pregunta: `${t('formularios.f058a.gijonTitulo')} — ${t('formularios.f058a.fecha')}`, respuesta: e.gijonFecha });
  pares.push(...paresDeEscalaClinica(DEFINICION_GIJON, e.gijon, `${t('formularios.f058a.gijonTitulo')} — ${t('formularios.f058a.total')}`));

  pares.push(...paresDeEscalaClinica(DEFINICION_ZARIT, e.zarit, `${t('formularios.f058a.zaritTitulo')} — ${t('formularios.f058a.total')}`));

  pares.push(...paresProfesionalPorTurno(t, 'formularios.f058a.profesionalHojas', CAMPOS_PROFESIONAL_PAPEL, e.profesional, HOJAS_PROFESIONAL_058A));

  return pares.filter((p) => p.respuesta.trim() !== '');
}
